<script lang="ts">
    import { Select, type Selected } from 'bits-ui';
    import { createKey, createVariantMap, getUniqueOptionIds } from '$lib/util/productUtil';
    import type { ApiSchemas } from '$lib/api/shopwareApi';
    import { useLocaleISOContext, usePropertiesContext } from '$lib/context';
    import { unknown } from 'valibot';

    // TODO: Optimize once there is time to do it (low priority)

    const properties = usePropertiesContext();
    const isoCode = useLocaleISOContext();

    export let variants: ApiSchemas['Product'][];
    export let slug: string;
    export let id: string;
    export let side: 'left' | 'right' | 'top' | 'bottom' = 'bottom';

    let classes = '';
    export { classes as class };

    $: variantMap = createVariantMap(variants);

    $: if (selectedSize || selectedYear) {
        const variant = variantMap[createKey([selectedSize.value, selectedYear.value])];
        if (slug !== variant?.productNumber) {
            slug = variant?.productNumber ?? '';
            id = variant?.id ?? '';
        }
    }

    let selectedSize: Selected<string>;
    updateSelectedSize(variants);

    function updateSelectedSize(variants: ApiSchemas['Product'][]) {
        selectedSize = {
            label: properties.size.options[
                variants?.[0].options.find(
                    (option: ApiSchemas['Product']['options']) => option.groupId === properties.size.id
                ).id
            ][isoCode],
            value: variants?.[0].options.find(
                (option: ApiSchemas['Product']['options']) => option.groupId === properties.size.id
            ).id
        };
    }

    let selectedYear: Selected<string>;
    updateSelectedYear(variants);

    function updateSelectedYear(variants: ApiSchemas['Product'][]) {
        selectedYear = {
            label: properties.year.options[
                variants?.[0].options.find(
                    (option: ApiSchemas['Product']['options']) => option.groupId === properties.year.id
                ).id
            ][isoCode],
            value: variants?.[0].options.find(
                (option: ApiSchemas['Product']['options']) => option.groupId === properties.year.id
            ).id
        };
    }

    let availableSizes: string[];
    $: updateAvailableSizes(variants, selectedYear);

    function updateAvailableSizes(variants: ApiSchemas['Product'][], selectedYear: Selected<string>) {
        availableSizes = getUniqueOptionIds(
            variants.filter(
                (variant) =>
                    selectedYear.value ===
                    variant.options.find(
                        (option: ApiSchemas['Product']['options']) => option.groupId === properties.year.id
                    ).id
            ),
            properties.size.id
        );
    }

    let availableYears: string[];
    $: updateAvailableYears(variants, selectedSize);

    function updateAvailableYears(variants: ApiSchemas['Product'][], selectedSize: Selected<string>) {
        availableYears = getUniqueOptionIds(
            variants.filter(
                (variant) =>
                    selectedSize.value ===
                    variant.options.find(
                        (option: ApiSchemas['Product']['options']) => option.groupId === properties.size.id
                    ).id
            ),
            properties.year.id
        );
    }
</script>

<div class={classes}>
    <!-- SIZE -->
    <Select.Root
        bind:selected={selectedSize}
        items={availableSizes?.map((size) => ({
            label: properties.size.options[size][isoCode],
            value: size
        }))}
        preventScroll={false}
    >
        <Select.Trigger
            class="py-2 text-sm tracking-[0.07em] text-primary font-medium flex items-center gap-2 select-none {availableSizes.length >
            1
                ? 'px-3.5 border border-primary hover:border-darkgold hover:text-darkgold transition-colors group'
                : 'px-5 bg-darkchamois pointer-events-none'} "
        >
            <Select.Value />
            {#if availableSizes.length > 1}
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path
                        d="M11 0.5L5.99999 5.5L1 0.5"
                        class="stroke-primary group-hover:stroke-darkgold transition-colors"
                        stroke-width="1.2"
                    />
                </svg>
            {/if}
        </Select.Trigger>
        <Select.Content {side} class="z-50">
            {#if availableSizes.length > 1}
                {#each availableSizes ?? [] as sizeId}
                    <Select.Item
                        class="text-sm text-primary px-3.5 py-2 bg-white select-none font-medium cursor-pointer hover:bg-neutral-50"
                        value={sizeId}
                        label={properties.size.options[sizeId][isoCode]}
                    >
                        {properties.size.options[sizeId][isoCode]}
                        <Select.ItemIndicator />
                    </Select.Item>
                {/each}
            {/if}
        </Select.Content>
    </Select.Root>

    <!-- YEAR -->
    {#if properties.year.options[selectedYear.value]['en-GB'] !== 'unknown'}
        <Select.Root
            bind:selected={selectedYear}
            items={availableYears?.map((year) => ({
                label: properties.year.options[year][isoCode],
                value: year
            }))}
            preventScroll={false}
        >
            <Select.Trigger
                class="py-2 text-sm tracking-[0.07em] text-primary font-medium flex items-center gap-2 select-none {availableYears.length >
                1
                    ? 'px-3.5 border border-primary hover:border-darkgold hover:text-darkgold transition-colors group'
                    : 'px-5 bg-darkchamois pointer-events-none'} "
            >
                <Select.Value />
                {#if availableYears.length > 1}
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                        <path
                            d="M11 0.5L5.99999 5.5L1 0.5"
                            class="stroke-primary group-hover:stroke-darkgold transition-colors"
                            stroke-width="1.2"
                        />
                    </svg>
                {/if}
            </Select.Trigger>
            <Select.Content {side} class="z-50">
                {#if availableYears.length > 1}
                    {#each availableYears ?? [] as yearId}
                        <Select.Item
                            class="text-sm text-primary px-3.5 py-2 bg-white select-none font-medium cursor-pointer hover:bg-neutral-50"
                            value={yearId}
                            label={properties.year.options[yearId][isoCode]}
                        >
                            {properties.year.options[yearId][isoCode]}
                            <Select.ItemIndicator />
                        </Select.Item>
                    {/each}
                {/if}
            </Select.Content>
        </Select.Root>
    {/if}
</div>
